<template>
	<VDropdown
		:shown="isOpen"
		placement="right-start"
		distance="8"
		:skidding="-8"
		:disabled="!isDropdownEnabled"
		@auto-hide="$emit('toggle-open')"
		@apply-hide="$emit('update:is-open', false)"
	>
		<div class="user-assign-select__avatar">
			<SuiAvatar
				v-if="dynamicOwnerInitials"
				v-tooltip="{
					content: dynamicOwnerLangVar,
					popperClass: `user-assign-select-owner-tooltip`,
				}"
				as="button"
				variant="dashed"
				color="#717a89"
				:initials="dynamicOwnerInitials"
				:disabled="true"
				size="x-small"
			/>
			<SuiAvatar
				v-else
				v-tooltip="{
					content: ownerTooltip,
					popperClass: `user-assign-select-owner-tooltip`,
				}"
				as="button"
				type="button"
				:disabled="!isDropdownEnabled"
				:url="ownerProfileImage"
				:aria-label="owner?.name"
				:size="avatarSize"
				show-border
				:initials="owner?.initials || ''"
				:color="owner?.hslColor || ''"
				@click="$emit('avatar-click')"
				@dblclick="$emit('avatar-dblclick')"
				@keypress="$emit('avatar-click')"
			/>
			<div
				v-if="size === UserAssignSelectSize.LARGE"
				class="user-assign-select__avatar-name"
			>
				{{ owner?.firstName }}
			</div>
		</div>
		<template #popper>
			<UserAssignSelectOwnerDropdownPopper
				:is-open="isOpen"
				:is-modify-enabled="isModifyEnabled"
				:owner="owner"
				:entity-owner="entityOwner"
				:preloaded-users="preloadedUsers"
				:enable-role-selection="enableRoleSelection"
				:task-id="taskId"
				:project-id="projectId"
				:contact-id="contactId"
				:load-project-members="loadProjectMembers"
				:owner-title="ownerTitle"
				@toggle-open="$emit('toggle-open')"
				@owner-click="$emit('owner-click', $event)"
				@assign-owner="$emit('assign-owner')"
			/>
		</template>
	</VDropdown>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { SuiAvatar } from '@developers/scoro-ui';
import {
	type UserAssignSelectUserFragment,
	type Project,
	type Contact,
	type Task,
	DynamicOwnerEnum,
} from '@/generated/graphql';
import { UserAssignSelectSize } from '../../constants';
import { type ValueOf } from '@/types';
import { type UserAssignAvatarSize } from '../../types';
import { useTranslation } from '@/composables/useTranslation';
import { getInitials, getUserTooltip, getUserProfileImage } from '../../utils';
import UserAssignSelectOwnerDropdownPopper from './UserAssignSelectOwnerDropdownPopper.vue';

export interface UserAssignSelectOwnerDropdownProps {
	isOpen: boolean;
	isDropdownEnabled: boolean;
	isModifyEnabled: boolean;
	owner: UserAssignSelectUserFragment | null | undefined;
	dynamicOwner?: DynamicOwnerEnum | null;
	entityOwner: UserAssignSelectUserFragment | null | undefined;
	preloadedUsers?: UserAssignSelectUserFragment[];
	enableRoleSelection?: boolean;
	taskId?: Task['id'];
	projectId?: Project['id'];
	contactId?: Contact['id'];
	loadProjectMembers?: boolean;
	ownerTitle?: string;
	avatarSize: UserAssignAvatarSize;
	size: ValueOf<typeof UserAssignSelectSize>;
}

const { owner, dynamicOwner, size } =
	defineProps<UserAssignSelectOwnerDropdownProps>();

defineEmits<{
	'update:is-open': [value: boolean];
	'toggle-open': [];
	'avatar-click': [];
	'avatar-dblclick': [];
	'owner-click': [user: UserAssignSelectUserFragment];
	'assign-owner': [];
}>();

const { t } = useTranslation();

const dynamicOwnerLangVar = computed(() => {
	if (!dynamicOwner) {
		return null;
	}

	return dynamicOwner === DynamicOwnerEnum.TaskCreator
		? t('task_creator')
		: t('project_manager');
});

const dynamicOwnerInitials = computed(() => {
	if (!dynamicOwnerLangVar.value) {
		return null;
	}

	return getInitials(dynamicOwnerLangVar.value);
});

const ownerTooltip = computed(() => getUserTooltip(owner));

const ownerProfileImage = computed(() => getUserProfileImage(owner, size));
</script>
